import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { fmtErr } from "../../lib/api";
import { Card, EmptyState, Field, Spinner, Stat, StatusChip, TypeChip, fmtMoney, inputCls } from "../ui";

const TABS = ["Requests", "Market Checks", "Suppliers", "Customers", "Commercial"];
const BUCKETS = [
  { key: null, label: "All" },
  { key: "new", label: "New" },
  { key: "sourcing", label: "Sourcing" },
  { key: "awaiting", label: "Awaiting Approval" },
  { key: "fulfillment", label: "Fulfillment" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

function Requests({ fixedBucket }) {
  const [bucket, setBucket] = useState(fixedBucket ?? null);
  const [items, setItems] = useState(null);
  useEffect(() => {
    setItems(null);
    api.get("/admin/requests", { params: bucket ? { bucket } : {} }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [bucket]);
  return (
    <div>
      {!fixedBucket && (
      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-bucket-filters">
        {BUCKETS.map((b) => (
          <button
            key={b.label}
            data-testid={`bucket-${b.key || "all"}`}
            onClick={() => setBucket(b.key)}
            className={`text-xs font-outfit rounded-full px-4 py-2 border transition-colors ${bucket === b.key ? "border-gold/60 text-gold bg-gold/10" : "border-white/10 text-white/50 hover:text-white"}`}
          >
            {b.label}
          </button>
        ))}
      </div>
      )}
      {fixedBucket && (
        <p className="text-sm text-white/45 mb-6" data-testid="market-check-intro">
          Market-check requests — benchmark work for the day. Open one to source options and send a proposal.
        </p>
      )}
      {!items ? <div className="py-16"><Spinner /></div> : items.length === 0 ? (
        <EmptyState title="No requests" text="Procurement requests in this view will appear here." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-requests-table">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-white/35 border-b border-white/8">
                <th className="pb-3 pr-4">Request</th><th className="pb-3 pr-4">Customer</th><th className="pb-3 pr-4">Product</th><th className="pb-3 pr-4">Type</th><th className="pb-3 pr-4">Status</th><th className="pb-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="py-4 pr-4">
                    <Link to={`/admin/requests/${r.id}`} className="text-gold hover:text-goldbright font-outfit" data-testid={`admin-request-link-${r.ref}`}>{r.ref}</Link>
                  </td>
                  <td className="py-4 pr-4 text-white/75">{r.company_name}</td>
                  <td className="py-4 pr-4 text-white/75">{r.product}</td>
                  <td className="py-4 pr-4"><TypeChip type={r.type} /></td>
                  <td className="py-4 pr-4"><StatusChip status={r.status} /></td>
                  <td className="py-4 text-white/40 capitalize">{r.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function genPassword() {
  return `Ajemi#${Math.random().toString(36).slice(2, 8)}${Math.floor(Math.random() * 90 + 10)}`;
}

function Suppliers() {
  const [items, setItems] = useState(null);
  const [perf, setPerf] = useState([]);
  const [apps, setApps] = useState([]);
  const [convertTarget, setConvertTarget] = useState(null);
  const [convertForm, setConvertForm] = useState(null);
  const [convertResult, setConvertResult] = useState(null);
  const [converting, setConverting] = useState(false);

  const load = () => {
    api.get("/admin/suppliers").then((r) => setItems(r.data.items)).catch(() => setItems([]));
    api.get("/admin/supplier-applications").then((r) => setApps(r.data.items)).catch(() => setApps([]));
    api.get("/admin/supplier-performance").then((r) => setPerf(r.data.items)).catch(() => setPerf([]));
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.post(`/admin/suppliers/${id}/status`, { status });
    load();
  };

  const openConvert = (app) => {
    setConvertTarget(app);
    setConvertResult(null);
    setConvertForm({
      company_name: app.company_name || "",
      contact_name: app.contact_name || "",
      email: app.email || "",
      phone: app.phone || "",
      password: genPassword(),
    });
  };

  const doConvert = async (e) => {
    e.preventDefault();
    if (converting) return;
    setConverting(true);
    try {
      const res = await api.post(`/admin/supplier-applications/${convertTarget.id}/convert`, convertForm);
      setConvertResult(res.data.user);
      toast.success("Supplier account created", { description: res.data.user.email });
      load();
    } catch (err) {
      toast.error("Conversion failed", { description: fmtErr(err) });
    } finally {
      setConverting(false);
    }
  };

  if (!items) return <div className="py-16"><Spinner /></div>;
  return (
    <div>
      <Card title="Supplier Performance" className="mb-8" testId="supplier-performance-card">
        {perf.length === 0 ? (
          <p className="text-sm text-white/40">No performance data yet — metrics build automatically from real opportunities, offers and deliveries.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="supplier-performance-table">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-white/35 border-b border-white/8">
                  <th className="pb-3 pr-4">Supplier</th><th className="pb-3 pr-4">Opportunities</th><th className="pb-3 pr-4">Offers</th><th className="pb-3 pr-4">Awarded</th><th className="pb-3 pr-4">Win Rate</th><th className="pb-3 pr-4">Avg Response</th><th className="pb-3 pr-4">Delivered</th><th className="pb-3">Fulfillment</th>
                </tr>
              </thead>
              <tbody>
                {perf.map((p) => (
                  <tr key={p.supplier_id} className="border-b border-white/5">
                    <td className="py-4 pr-4 text-white/85">{p.company_name}</td>
                    <td className="py-4 pr-4 text-white/60">{p.opportunities}</td>
                    <td className="py-4 pr-4 text-white/60">{p.offers}</td>
                    <td className="py-4 pr-4 text-white/60">{p.awarded}</td>
                    <td className="py-4 pr-4 text-white/75">{p.win_rate != null ? `${p.win_rate}%` : "—"}</td>
                    <td className="py-4 pr-4 text-white/75">{p.avg_response_hours != null ? `${p.avg_response_hours}h` : "—"}</td>
                    <td className="py-4 pr-4 text-white/75">{p.delivered_volume ? Number(p.delivered_volume).toLocaleString() : "—"}</td>
                    <td className="py-4 text-white/75">{p.fulfillment_rate != null ? `${p.fulfillment_rate}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {apps.filter((a) => a.status !== "converted").length > 0 && (
        <Card title="Website Supplier Applications" className="mb-8" testId="supplier-applications-card">
          <div className="space-y-3">
            {apps.filter((a) => a.status !== "converted").map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 rounded-xl p-4" data-testid={`supplier-application-${a.reference_id}`}>
                <div>
                  <p className="text-white font-outfit">{a.company_name}</p>
                  <p className="text-xs text-white/40 mt-0.5">
                    {a.contact_name} · {a.email} · {a.products_supplied || "—"} · {a.locations_served || "—"}
                  </p>
                  <p className="text-[11px] text-white/30 mt-0.5">{a.reference_id} · {new Date(a.created_at).toLocaleDateString()}</p>
                </div>
                <button
                  onClick={() => openConvert(a)}
                  className="border border-gold/50 text-gold text-xs font-outfit rounded-full px-5 py-2 hover:bg-gold hover:text-ink transition-colors"
                  data-testid={`convert-application-${a.reference_id}`}
                >
                  Convert to Portal Supplier
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}
      {items.length === 0 ? <EmptyState title="No suppliers yet" text="Registered supplier accounts will appear here for review." /> : (
    <div className="overflow-x-auto" data-testid="admin-suppliers-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-white/35 border-b border-white/8">
            <th className="pb-3 pr-4">Company</th><th className="pb-3 pr-4">Contact</th><th className="pb-3 pr-4">Products</th><th className="pb-3 pr-4">Status</th><th className="pb-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-b border-white/5">
              <td className="py-4 pr-4 text-white/85">{s.company_name}<p className="text-xs text-white/35">{s.email}</p></td>
              <td className="py-4 pr-4 text-white/60">{s.contact_name}</td>
              <td className="py-4 pr-4 text-white/60 max-w-[200px] truncate">{s.profile?.products_supplied || "—"}</td>
              <td className="py-4 pr-4">
                <span className={`text-[10px] tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${s.status === "active" ? "border-emerald-400/50 text-emerald-300" : s.status === "pending" ? "border-gold/50 text-gold" : "border-red-400/40 text-red-300"}`}>{s.status}</span>
              </td>
              <td className="py-4">
                <div className="flex gap-2">
                  {s.status !== "active" && (
                    <button onClick={() => setStatus(s.id, "active")} className="text-xs text-emerald-300 border border-emerald-400/40 rounded-full px-3 py-1 hover:bg-emerald-400/10 transition-colors" data-testid={`approve-supplier-${s.id}`}>Approve</button>
                  )}
                  {s.status === "active" && (
                    <button onClick={() => setStatus(s.id, "suspended")} className="text-xs text-red-300 border border-red-400/40 rounded-full px-3 py-1 hover:bg-red-400/10 transition-colors" data-testid={`suspend-supplier-${s.id}`}>Suspend</button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
      )}

      <Dialog open={!!convertTarget} onOpenChange={(o) => { if (!o) setConvertTarget(null); }}>
        <DialogContent className="bg-surface border-white/10 text-white sm:max-w-lg" data-testid="convert-supplier-dialog" data-lenis-prevent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-2xl font-light">Convert to Portal Supplier</DialogTitle>
            <DialogDescription className="text-white/50 text-sm">
              Create a supplier portal account from {convertTarget?.company_name}&rsquo;s application. The account is
              active immediately; share the password with the supplier.
            </DialogDescription>
          </DialogHeader>
          {convertResult ? (
            <div className="mt-4 border border-emerald-400/30 rounded-xl p-5" data-testid="convert-success">
              <p className="text-emerald-300 font-outfit">Account created</p>
              <p className="text-sm text-white/70 mt-2">Email: {convertResult.email}</p>
              <p className="text-sm text-white/70">Temporary password: <span className="text-gold font-mono">{convertForm.password}</span></p>
              <p className="text-xs text-white/40 mt-3">Application marked as converted and linked to this account.</p>
            </div>
          ) : convertForm ? (
            <form onSubmit={doConvert} className="grid grid-cols-1 gap-4 mt-4" data-testid="convert-supplier-form">
              <Field label="Company Name" required><input required value={convertForm.company_name} onChange={(e) => setConvertForm((f) => ({ ...f, company_name: e.target.value }))} className={inputCls} data-testid="convert-company-input" /></Field>
              <Field label="Contact Name" required><input required value={convertForm.contact_name} onChange={(e) => setConvertForm((f) => ({ ...f, contact_name: e.target.value }))} className={inputCls} data-testid="convert-contact-input" /></Field>
              <Field label="Email" required><input required type="email" value={convertForm.email} onChange={(e) => setConvertForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} data-testid="convert-email-input" /></Field>
              <Field label="Phone"><input value={convertForm.phone} onChange={(e) => setConvertForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} data-testid="convert-phone-input" /></Field>
              <Field label="Temporary Password" required><input required minLength={8} value={convertForm.password} onChange={(e) => setConvertForm((f) => ({ ...f, password: e.target.value }))} className={`${inputCls} font-mono`} data-testid="convert-password-input" /></Field>
              <button type="submit" disabled={converting} className="bg-gold text-ink font-outfit font-medium rounded-full py-3 hover:bg-goldbright transition-colors disabled:opacity-60" data-testid="convert-submit-btn">
                {converting ? "Creating…" : "Create Supplier Account"}
              </button>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Customers() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/customers").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (!items) return <div className="py-16"><Spinner /></div>;
  if (items.length === 0) return <EmptyState title="No customers yet" text="Registered customer accounts will appear here." />;
  return (
    <div className="overflow-x-auto" data-testid="admin-customers-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-white/35 border-b border-white/8">
            <th className="pb-3 pr-4">Company</th><th className="pb-3 pr-4">Contact</th><th className="pb-3 pr-4">Requests</th><th className="pb-3">Flags</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-b border-white/5">
              <td className="py-4 pr-4 text-white/85">{c.company_name}<p className="text-xs text-white/35">{c.email}</p></td>
              <td className="py-4 pr-4 text-white/60">{c.contact_name}</td>
              <td className="py-4 pr-4 text-white/60">{c.request_count}</td>
              <td className="py-4 space-x-2">
                {c.recurring && <span className="text-[10px] tracking-[0.15em] uppercase border border-gold/40 text-gold rounded-full px-3 py-1">Recurring</span>}
                {c.international && <span className="text-[10px] tracking-[0.15em] uppercase border border-volt/40 text-volt rounded-full px-3 py-1">International</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Commercial() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/commercials").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (!items) return <div className="py-16"><Spinner /></div>;
  if (items.length === 0) return <EmptyState title="No commercial records" text="Record supplier price, Ajemi margin/fee and customer price on any request to build commercial records." />;
  return (
    <div className="overflow-x-auto" data-testid="admin-commercial-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-white/35 border-b border-white/8">
            <th className="pb-3 pr-4">Request</th><th className="pb-3 pr-4">Model</th><th className="pb-3 pr-4">Supplier Price</th><th className="pb-3 pr-4">Ajemi Margin/Fee</th><th className="pb-3 pr-4">Customer Price</th><th className="pb-3 pr-4">Volume</th><th className="pb-3">Est. Gross Margin</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id || c.request_id} className="border-b border-white/5">
              <td className="py-4 pr-4 text-gold font-outfit">{c.ref}<p className="text-xs text-white/35">{c.company_name}</p></td>
              <td className="py-4 pr-4 text-white/60 capitalize">{c.model}</td>
              <td className="py-4 pr-4 text-white/75">{fmtMoney(c.supplier_price)}</td>
              <td className="py-4 pr-4 text-white/75">{fmtMoney(c.ajemi_margin)}</td>
              <td className="py-4 pr-4 text-white/75">{fmtMoney(c.customer_price)}</td>
              <td className="py-4 pr-4 text-white/75">{Number(c.volume).toLocaleString()}</td>
              <td className="py-4 text-emerald-300">{fmtMoney(c.est_gross_margin)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("Requests");
  const [ov, setOv] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setOv(r.data)).catch(() => setOv(false)); }, []);

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-outfit text-3xl font-extralight tracking-tight text-white mb-8">
        Ajemi <span className="text-gold">Procurement Control</span>
      </h1>
      {ov && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Stat label="New Requests" value={ov.requests.new} testId="admin-stat-new" />
          <Stat label="Awaiting Approval" value={ov.requests.awaiting} testId="admin-stat-awaiting" />
          <Stat label="In Fulfillment" value={ov.requests.fulfillment} testId="admin-stat-fulfillment" />
          <Stat label="Est. Margin (₦)" value={Number(ov.commercial.est_margin_total).toLocaleString()} testId="admin-stat-margin" />
          <Stat label="Active Suppliers" value={ov.suppliers.active} testId="admin-stat-suppliers" />
          <Stat label="Pending Suppliers" value={ov.suppliers.pending} testId="admin-stat-pending-suppliers" />
          <Stat label="Customers" value={ov.customers.total} testId="admin-stat-customers" />
          <Stat label="Completed" value={ov.requests.completed} testId="admin-stat-completed" />
        </div>
      )}
      <div className="flex gap-2 mb-8 border-b border-white/8 overflow-x-auto" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t.toLowerCase()}`}
            onClick={() => setTab(t)}
            className={`px-5 py-3 text-sm font-outfit border-b-2 transition-colors -mb-px whitespace-nowrap ${tab === t ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-white"}`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "Requests" && <Requests />}
      {tab === "Market Checks" && <Requests fixedBucket="market_check" />}
      {tab === "Suppliers" && <Suppliers />}
      {tab === "Customers" && <Customers />}
      {tab === "Commercial" && <Commercial />}
    </div>
  );
}
